import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { useScrollReveal, useScrollProgress } from '@/hooks/useScrollReveal';
import './TeamOverview.css';

interface StrengthItem {
  title: string;
  icon: string;
  score_label: string;
  description: string;
}

interface GrowthItem {
  title: string;
  icon: string;
  description: string;
  unlock_statement: string;
}

interface PillarItem {
  number: string;
  title: string;
  description: string;
}

interface TeamOverviewData {
  organization: {
    name: string;
    report_date: string;
    retake_date: string;
  };
  team_identity: {
    member_count: number;
    family_distribution: Record<string, number>;
    built_for_statement: string;
    team_health_label: string;
  };
  collective_strengths: StrengthItem[];
  combined_implication: string;
  growth_areas: GrowthItem[];
  shared_commitment: string;
  pressure_pattern: {
    team_description: string;
    collective_move: string;
  };
  next_90_days: {
    pillars: PillarItem[];
    because_statement_prompt: string;
    because_statement_suggestion: string;
    retake_date: string;
    retake_message: string;
  };
  session_guide: {
    recommended_duration: string;
    suggested_agenda: string[];
  };
}

const FAMILY_COLORS: Record<string, string> = {
  connector: '#2ECC87',
  regulator: '#2040B0',
  driver: '#C04040',
  strategist: '#7B3FA0',
};

const FAMILY_DESCRIPTORS: Record<string, string> = {
  connector: 'build trust and create belonging',
  driver: 'create urgency and drive results',
  strategist: 'see the system and solve for clarity',
  regulator: 'hold the room and stabilize under pressure',
};

function familyKey(name: string): string {
  return name.toLowerCase().replace(/s$/, '');
}

function familyDisplay(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

const SESSION_AGENDA = [
  {
    time: '00:00 — 00:10',
    title: 'Who We Are (Section A)',
    notes: 'Leader reads the "We are built for..." statement aloud.\nAsk: "Does this feel true to you? What would you add?"',
  },
  {
    time: '00:10 — 00:20',
    title: 'What We Are Great At (Section B)',
    notes: 'Name the two strengths. Give specific examples from recent work.\nAsk: "When have you seen this show up at its best?"',
  },
  {
    time: '00:20 — 00:35',
    title: 'What We Are Working On (Section C)',
    notes: 'Name the growth areas without drama.\nAsk: "What would it look like if we were 10% better at this?"\nRead the Shared Commitment together.',
  },
  {
    time: '00:35 — 00:45',
    title: 'Under Pressure (Section D)',
    notes: 'Read the pressure pattern paragraph aloud.\nAsk: "Does this ring true? What would you add or change?"\nDiscuss the "What We Do About It" statement.',
  },
  {
    time: '00:45 — 01:00',
    title: 'Our Next 90 Days (Section E)',
    notes: 'Walk through the three commitment pillars.\nComplete the Because statement together — go around the room.\nClose with the retake date and what you are all tracking.',
  },
];

export default function TeamOverview() {
  const { orgId } = useParams<{ orgId: string }>();
  const [data, setData] = useState<TeamOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const isPrintMode = new URLSearchParams(window.location.search).get('print') === '1';

  const observe = useScrollReveal(0.07);
  useScrollProgress('tov-progress-bar');

  const revealRef = useCallback((el: HTMLElement | null) => {
    if (el) observe(el);
  }, [observe]);

  useEffect(() => {
    fetch(`/api/team/${orgId}/overview`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 403 ? 'This team needs at least 3 completed assessments to generate an overview.' : 'Failed to load team overview');
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [orgId]);

  const handlePdfDownload = async () => {
    setGeneratingPdf(true);
    try {
      const res = await fetch(`/api/team/${orgId}/overview/pdf`);
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EQUIP360-Team-Overview-${data?.organization.name || 'Report'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Could not generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="tov-loading">
        <div className="tov-loading-spinner" />
        <span>Loading team overview...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tov-error">
        <span style={{ fontSize: 32 }}>&#9888;</span>
        <span>{error}</span>
      </div>
    );
  }

  if (!data) return null;

  const { organization, team_identity, collective_strengths, combined_implication, growth_areas, shared_commitment, pressure_pattern, next_90_days, session_guide } = data;

  const normalizedDist: Record<string, number> = {};
  for (const [k, v] of Object.entries(team_identity.family_distribution)) {
    normalizedDist[familyKey(k)] = (normalizedDist[familyKey(k)] || 0) + v;
  }
  const familyEntries = Object.entries(normalizedDist).filter(([, count]) => count > 0);
  const totalMembers = team_identity.member_count || familyEntries.reduce((s, [, c]) => s + c, 0) || 1;
  const allFamilies = ['connector', 'driver', 'strategist', 'regulator'];

  return (
    <div className={`tov-report${isPrintMode ? ' tov-print-mode' : ''}`}>
      <div id="tov-progress-bar" />

      <nav className="tov-nav no-print">
        <div className="tov-nav-left">
          <span className="tov-nav-logo">E.Q.U.I.P. 360</span>
          <span className="tov-nav-divider" />
          <span className="tov-nav-title">Team Overview</span>
        </div>
        <div className="tov-nav-links">
          <button className="tov-nav-link" onClick={() => scrollTo('tov-strengths')}>Strengths</button>
          <button className="tov-nav-link" onClick={() => scrollTo('tov-growth')}>Growth</button>
          <button className="tov-nav-link" onClick={() => scrollTo('tov-pressure')}>Pressure</button>
          <button className="tov-nav-link" onClick={() => scrollTo('tov-next')}>Next 90 Days</button>
        </div>
      </nav>

      {/* SECTION A — WHO WE ARE */}
      <section className="tov-section tov-hero tov-section-a">
        <div className="tov-hero-badge">Team Intelligence Overview</div>
        <h1 className="tov-section-title">{organization.name}</h1>
        <div className="tov-hero-meta">
          {organization.report_date} &middot; {team_identity.member_count} Team Members &middot; {team_identity.team_health_label}
        </div>
        <div className="tov-divider" />

        <div className="tov-family-legend">
          {familyEntries.map(([fKey, count]) => (
            <div key={fKey} className="tov-family-chip">
              <span className="tov-family-dot" style={{ background: FAMILY_COLORS[fKey] || '#888' }} />
              {familyDisplay(fKey)}s ({count})
            </div>
          ))}
        </div>

        <div className="tov-built-for">{team_identity.built_for_statement}</div>

        <div className="tov-family-bars">
          {allFamilies.map(fKey => {
            const count = normalizedDist[fKey] || 0;
            const pct = Math.round((count / totalMembers) * 100);
            return (
              <div key={fKey} className="tov-fbar-row">
                <div className="tov-fbar-label">
                  <span className="tov-fbar-dot" style={{ background: FAMILY_COLORS[fKey] }} />
                  <span className="tov-fbar-name">{familyDisplay(fKey)}s</span>
                  <span className="tov-fbar-desc">{FAMILY_DESCRIPTORS[fKey]}</span>
                </div>
                <div className="tov-fbar-track">
                  <div
                    className="tov-fbar-fill"
                    style={{ width: `${pct}%`, background: FAMILY_COLORS[fKey] }}
                  />
                </div>
                <span className="tov-fbar-value">{count} ({pct}%)</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* SECTION B — WHAT WE ARE GENUINELY GREAT AT */}
      <section id="tov-strengths" className="tov-section tov-section-b">
        <div ref={revealRef} className="tov-reveal">
          <div className="tov-section-tag">Collective Strengths</div>
          <h2 className="tov-section-title">What We Are Genuinely Great At</h2>
          <p className="tov-section-sub">These are the emotional intelligence strengths that define how this team leads, collaborates, and shows up.</p>
        </div>

        <div className="tov-strength-grid">
          {collective_strengths.map((s, i) => (
            <div key={i} ref={revealRef} className="tov-reveal tov-strength-card">
              <div className="tov-strength-accent" style={{ background: i === 0 ? 'var(--tov-green)' : 'var(--tov-blue)' }} />
              <div className="tov-strength-icon">{s.icon}</div>
              <div className="tov-strength-name">{s.title}</div>
              <div className="tov-strength-tier">{s.score_label}</div>
              <div className="tov-strength-desc">{s.description}</div>
            </div>
          ))}
        </div>

        <div ref={revealRef} className="tov-reveal tov-implication">{combined_implication}</div>
      </section>

      {/* SECTION C — WHAT WE ARE WORKING ON TOGETHER */}
      <section id="tov-growth" className="tov-section tov-section-c">
        <div ref={revealRef} className="tov-reveal">
          <div className="tov-section-tag">Growth Areas</div>
          <h2 className="tov-section-title">What We Are Working On Together</h2>
          <p className="tov-section-sub">Every strong team has edges to sharpen. These are the areas where focused effort will create the most impact.</p>
        </div>

        <div className="tov-growth-grid">
          {growth_areas.map((g, i) => (
            <div key={i} ref={revealRef} className={`tov-reveal tov-growth-card ${i === 0 ? 'tov-growth-amber' : 'tov-growth-blue'}`}>
              <div className="tov-growth-icon">{g.icon}</div>
              <div className="tov-growth-name">{g.title}</div>
              <div className="tov-growth-desc">{g.description}</div>
              <div className="tov-growth-unlock">{g.unlock_statement}</div>
            </div>
          ))}
        </div>

        <div ref={revealRef} className="tov-reveal tov-commitment">
          <div className="tov-commitment-label">Shared Commitment</div>
          {shared_commitment}
        </div>
      </section>

      {/* SECTION D — HOW WE SHOW UP UNDER PRESSURE */}
      <section id="tov-pressure" className="tov-section tov-section-d">
        <div ref={revealRef} className="tov-reveal">
          <div className="tov-section-tag">Under Pressure</div>
          <h2 className="tov-section-title">How We Show Up Under Pressure</h2>
          <p className="tov-section-sub">Pressure reveals patterns. Understanding ours is the first step to leading through it.</p>
        </div>

        <div className="tov-pressure-block">
          <div ref={revealRef} className="tov-reveal tov-pressure-card">
            <div className="tov-pressure-card-label">Our Pattern</div>
            <div className="tov-pressure-text">{pressure_pattern.team_description}</div>
          </div>
          <div ref={revealRef} className="tov-reveal tov-pressure-card tov-pressure-move">
            <div className="tov-pressure-card-label move">The Collective Move</div>
            <div className="tov-pressure-text">{pressure_pattern.collective_move}</div>
          </div>
        </div>
      </section>

      {/* SECTION E — OUR NEXT 90 DAYS */}
      <section id="tov-next" className="tov-section tov-section-e">
        <div ref={revealRef} className="tov-reveal">
          <div className="tov-section-tag">Next 90 Days</div>
          <h2 className="tov-section-title">Our Path Forward</h2>
          <p className="tov-section-sub">Three moves that will shift how this team leads together.</p>
        </div>

        <div className="tov-next-pillars">
          {next_90_days.pillars.map((p, i) => (
            <div key={i} ref={revealRef} className="tov-reveal tov-pillar-card">
              <div className="tov-pillar-number">{p.number}</div>
              <div className="tov-pillar-title">{p.title}</div>
              <div className="tov-pillar-desc">{p.description}</div>
            </div>
          ))}
        </div>

        <div ref={revealRef} className="tov-reveal tov-because">
          <div className="tov-because-prompt">{next_90_days.because_statement_prompt}</div>
          <div className="tov-because-suggestion">{next_90_days.because_statement_suggestion}</div>
        </div>

        <div ref={revealRef} className="tov-reveal tov-retake">
          <div className="tov-retake-date">Retake: {next_90_days.retake_date}</div>
          <div className="tov-retake-msg">{next_90_days.retake_message}</div>
        </div>
      </section>

      {/* SESSION GUIDE — COLLAPSIBLE, NO PRINT */}
      <div className="tov-session-wrapper no-print">
        <button
          className="tov-session-toggle"
          onClick={() => setGuideOpen(!guideOpen)}
          aria-expanded={guideOpen}
        >
          <span className="tov-session-toggle-text">Session Guide</span>
          <span className="tov-session-toggle-duration">{session_guide?.recommended_duration || '60 minutes'}</span>
          <span className={`tov-session-toggle-arrow ${guideOpen ? 'open' : ''}`}>&#9662;</span>
        </button>
        {guideOpen && (
          <div className="tov-session-body">
            {SESSION_AGENDA.map((item, i) => (
              <div key={i} className="tov-session-item">
                <div className="tov-session-time">{item.time}</div>
                <div className="tov-session-content">
                  <div className="tov-session-item-title">{item.title}</div>
                  <div className="tov-session-notes">
                    {item.notes.split('\n').map((line, j) => (
                      <div key={j}>{line}</div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA — PRINT + PDF */}
      <div className="tov-cta-block no-print">
        <div className="tov-cta-text">Share this with your team</div>
        <div className="tov-cta-buttons">
          <button className="tov-cta-btn tov-cta-print" onClick={() => window.print()}>
            &#128438; Print
          </button>
          <button className="tov-cta-btn" onClick={handlePdfDownload} disabled={generatingPdf}>
            {generatingPdf ? 'Generating...' : '\u2193 Download PDF'}
          </button>
        </div>
      </div>

      <footer className="tov-footer">
        <div className="tov-footer-logo">E.Q.U.I.P. 360</div>
        <div className="tov-footer-copy">&copy; {new Date().getFullYear()} Mick Hunt Official. All rights reserved.</div>
      </footer>
    </div>
  );
}
